import React, { useEffect, useState } from 'react';
import { Globe, ArrowRightLeft, Send, History, Users, CheckCircle2 } from 'lucide-react';
import { useAppContext, Transfer } from '../contexts/AppContext';
import { formatCurrency, formatForeignCurrency, formatDate, generateReference } from '../utils/formatters';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import { toast } from 'sonner';
const EXCHANGE_RATES: Record<string, number> = {
  // International
  USD: 1550,
  GBP: 1950,
  EUR: 1680,
  CAD: 1130,
  AUD: 1010,
  CHF: 1720,
  JPY: 10.3,
  CNY: 213,
  AED: 422,
  INR: 18.5,
  // African/Local
  ZAR: 83,
  GHS: 96,
  KES: 9.8,
  XOF: 2.56,
  EGP: 31.5,
  TZS: 0.58,
  UGX: 0.41,
  RWF: 1.12,
  XAF: 2.56,
  MAD: 155
};
const BANKS = ['GTBank', 'Access Bank', 'FirstBank', 'UBA', 'Zenith Bank', 'Kuda Bank', 'Opay'];
export const DiasporaExchange: React.FC = () => {
  const {
    state,
    dispatch,
    processTransaction
  } = useAppContext();
  const [activeTab, setActiveTab] = useState<'send' | 'beneficiaries' | 'history'>('send');
  // Form State
  const [currency, setCurrency] = useState('USD');
  const [amount, setAmount] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [bankName, setBankName] = useState(BANKS[0]);
  const [accountNumber, setAccountNumber] = useState('');
  const [purpose, setPurpose] = useState('Family Support');
  // UI State
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentRef, setCurrentRef] = useState('');
  // Calculations
  const numAmount = Number(amount) || 0;
  const rate = EXCHANGE_RATES[currency];
  const fee = numAmount * 0.015; // 1.5% fee
  const totalCharge = numAmount + fee;
  const ngnAmount = numAmount * rate;
  // Mock Beneficiaries (derived from past transfers)
  const beneficiaries = Array.from(new Set(state.transfers.map(t => JSON.stringify({
    name: t.recipientName,
    bank: t.bankName,
    account: t.accountNumber
  })))).map(s => JSON.parse(s));
  // Simulate account lookup
  useEffect(() => {
    if (accountNumber.length === 10) {
      // Mock lookup
      setRecipientName(accountNumber.startsWith('01') ? 'Chidinma Okafor' : 'Emeka Nwankwo');
    } else if (accountNumber.length < 10) {
      setRecipientName('');
    }
  }, [accountNumber]);
  const handleSendSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (numAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (accountNumber.length !== 10) {
      toast.error('Account number must be 10 digits');
      return;
    }
    setIsConfirmOpen(true);
  };
  const confirmTransfer = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setIsConfirmOpen(false);
      const reference = generateReference('TRF');
      setCurrentRef(reference);
      const newTransfer: Transfer = {
        id: `trf_${Date.now()}`,
        sourceCurrency: currency,
        sourceAmount: numAmount,
        ngnAmount: ngnAmount,
        recipientName,
        bankName,
        accountNumber,
        fee,
        exchangeRate: rate,
        status: 'Completed',
        date: new Date().toISOString(),
        reference
      };
      dispatch({
        type: 'ADD_TRANSFER',
        payload: newTransfer
      });
      // Note: In a real app, this might deduct from a linked foreign card.
      // For demo, we just record the transaction in the global history.
      dispatch({
        type: 'ADD_TRANSACTION',
        payload: {
          id: `tx_${Date.now()}`,
          type: 'Diaspora',
          title: `Transfer to ${recipientName}`,
          amount: -ngnAmount,
          status: 'Completed',
          date: new Date().toISOString(),
          reference
        }
      });
      setIsSuccessOpen(true);
      // Reset form
      setAmount('');
      setAccountNumber('');
      setRecipientName('');
    }, 1500);
  };
  const fillBeneficiary = (b: any) => {
    setRecipientName(b.name);
    setBankName(b.bank);
    setAccountNumber(b.account);
    setActiveTab('send');
  };
  return <div className="space-y-6" data-id="element-821">
      {/* Header */}
      <div data-id="element-822">
        <h1 className="text-2xl font-bold text-brand-text-dark" data-id="element-823">
          Foreign Exchange
        </h1>
        <p className="text-brand-text-light text-sm" data-id="element-824">
          Send and receive foreign currency with competitive rates.
        </p>
      </div>

      <div className="flex space-x-1 bg-white p-1 rounded-xl border border-gray-100 inline-flex" data-id="element-825">
        <button onClick={() => setActiveTab('send')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'send' ? 'bg-brand-primary/10 text-brand-primary-dark' : 'text-brand-text-light hover:bg-gray-50'}`} data-id="element-826">
          <Send size={16} data-id="element-827" /> Send Money
        </button>
        <button onClick={() => setActiveTab('beneficiaries')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'beneficiaries' ? 'bg-brand-primary/10 text-brand-primary-dark' : 'text-brand-text-light hover:bg-gray-50'}`} data-id="element-828">
          <Users size={16} data-id="element-829" /> Beneficiaries
        </button>
        <button onClick={() => setActiveTab('history')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'history' ? 'bg-brand-primary/10 text-brand-primary-dark' : 'text-brand-text-light hover:bg-gray-50'}`} data-id="element-830">
          <History size={16} data-id="element-831" /> History
        </button>
      </div>

      {activeTab === 'send' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-832">
          <div className="lg:col-span-2 bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-8" data-id="element-833">
            <form onSubmit={handleSendSubmit} className="space-y-8" data-id="element-834">
              {/* Calculator Section */}
              <div className="relative" data-id="element-835">
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100" data-id="element-836">
                  <label className="block text-sm font-medium text-gray-500 mb-2" data-id="element-837">
                    You Send
                  </label>
                  <div className="flex items-center gap-4" data-id="element-838">
                    <select value={currency} onChange={e => setCurrency(e.target.value)} className="bg-white border border-gray-200 rounded-xl px-4 py-3 font-semibold text-brand-text-dark focus:outline-none focus:ring-2 focus:ring-brand-primary/20" data-id="element-839">
                      <optgroup label="International" data-id="element-840">
                        <option value="USD" data-id="element-841">USD - US Dollar</option>
                        <option value="GBP" data-id="element-842">GBP - British Pound</option>
                        <option value="EUR" data-id="element-843">EUR - Euro</option>
                        <option value="CAD" data-id="element-844">CAD - Canadian Dollar</option>
                        <option value="AUD" data-id="element-845">AUD - Australian Dollar</option>
                        <option value="CHF" data-id="element-846">CHF - Swiss Franc</option>
                        <option value="JPY" data-id="element-847">JPY - Japanese Yen</option>
                        <option value="CNY" data-id="element-848">CNY - Chinese Yuan</option>
                        <option value="AED" data-id="element-849">AED - UAE Dirham</option>
                        <option value="INR" data-id="element-850">INR - Indian Rupee</option>
                      </optgroup>
                      <optgroup label="African/Local" data-id="element-851">
                        <option value="ZAR" data-id="element-852">ZAR - South African Rand</option>
                        <option value="GHS" data-id="element-853">GHS - Ghanaian Cedi</option>
                        <option value="KES" data-id="element-854">KES - Kenyan Shilling</option>
                        <option value="XOF" data-id="element-855">XOF - CFA Franc BCEAO</option>
                        <option value="EGP" data-id="element-856">EGP - Egyptian Pound</option>
                        <option value="TZS" data-id="element-857">TZS - Tanzanian Shilling</option>
                        <option value="UGX" data-id="element-858">UGX - Ugandan Shilling</option>
                        <option value="RWF" data-id="element-859">RWF - Rwandan Franc</option>
                        <option value="XAF" data-id="element-860">XAF - CFA Franc BEAC</option>
                        <option value="MAD" data-id="element-861">MAD - Moroccan Dirham</option>
                      </optgroup>
                    </select>
                    <input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="bg-transparent text-4xl font-bold text-brand-text-dark w-full focus:outline-none" placeholder="0.00" min="1" step="0.01" data-id="element-862" />
                  </div>
                </div>

                <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" data-id="element-863">
                  <div className="w-12 h-12 bg-white rounded-full border border-gray-100 shadow-sm flex items-center justify-center text-brand-primary" data-id="element-864">
                    <ArrowRightLeft size={20} className="rotate-90" data-id="element-865" />
                  </div>
                </div>

                <div className="bg-brand-primary/5 rounded-2xl p-5 border border-brand-primary/20 mt-2" data-id="element-866">
                  <label className="block text-sm font-medium text-brand-primary-dark mb-2" data-id="element-867">
                    Recipient Gets
                  </label>
                  <div className="flex items-center gap-4" data-id="element-868">
                    <div className="bg-white border border-brand-primary/20 rounded-xl px-4 py-3 font-semibold text-brand-text-dark flex items-center gap-2" data-id="element-869">
                      <img src="https://flagcdn.com/w20/ng.png" alt="NGN" className="w-5 h-auto rounded-sm" data-id="element-870" />{' '}
                      NGN
                    </div>
                    <input type="text" value={formatCurrency(ngnAmount).replace('₦', '')} readOnly className="bg-transparent text-4xl font-bold text-brand-text-dark w-full focus:outline-none" placeholder="0.00" data-id="element-871" />
                  </div>
                </div>
              </div>

              {/* Recipient Details */}
              <div className="space-y-5" data-id="element-872">
                <h3 className="font-semibold text-brand-text-dark border-b border-gray-100 pb-2" data-id="element-873">
                  Recipient Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-874">
                  <div data-id="element-875">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-876">
                      Bank Name
                    </label>
                    <select value={bankName} onChange={e => setBankName(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-877">
                      {BANKS.map(b => <option key={b} value={b} data-id="element-878">
                          {b}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-879">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-880">
                      Account Number
                    </label>
                    <input type="text" maxLength={10} value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" placeholder="10 digit number" data-id="element-881" />
                  </div>
                  <div className="md:col-span-2" data-id="element-882">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-883">
                      Account Name
                    </label>
                    <input type="text" value={recipientName} readOnly className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 outline-none" placeholder="Auto-filled on valid account" data-id="element-884" />
                  </div>
                  <div className="md:col-span-2" data-id="element-885">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-886">
                      Purpose of Transfer
                    </label>
                    <select value={purpose} onChange={e => setPurpose(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-887">
                      <option data-id="element-888">Family Support</option>
                      <option data-id="element-889">Business</option>
                      <option data-id="element-890">Savings</option>
                      <option data-id="element-891">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={!recipientName || numAmount <= 0} className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-4 rounded-xl font-semibold text-lg transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-892">
                Continue to Review <ArrowRightLeft size={20} data-id="element-893" />
              </button>
            </form>
          </div>

          {/* Rate Info Card */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 h-fit sticky top-24" data-id="element-894">
            <h3 className="font-semibold text-brand-text-dark mb-4 flex items-center gap-2" data-id="element-895">
              <Globe className="text-brand-primary" size={20} data-id="element-896" /> Exchange Rate
            </h3>
            <div className="bg-gray-50 rounded-xl p-4 mb-6" data-id="element-897">
              <p className="text-sm text-brand-text-light mb-1" data-id="element-898">Current Rate</p>
              <p className="text-2xl font-bold text-brand-text-dark" data-id="element-899">
                1 {currency} = ₦{rate.toLocaleString()}
              </p>
              <p className="text-xs text-brand-primary font-medium mt-2 flex items-center gap-1" data-id="element-900">
                <CheckCircle2 size={12} data-id="element-901" /> Guaranteed for 30 mins
              </p>
            </div>

            <div className="space-y-3 text-sm" data-id="element-902">
              <div className="flex justify-between" data-id="element-903">
                <span className="text-brand-text-light" data-id="element-904">Transfer Amount</span>
                <span className="font-medium" data-id="element-905">
                  {formatForeignCurrency(numAmount, currency)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-906">
                <span className="text-brand-text-light" data-id="element-907">
                  Transfer Fee (1.5%)
                </span>
                <span className="font-medium" data-id="element-908">
                  {formatForeignCurrency(fee, currency)}
                </span>
              </div>
              <div className="pt-3 border-t border-gray-100 flex justify-between font-semibold text-brand-text-dark" data-id="element-909">
                <span data-id="element-910">Total to Pay</span>
                <span data-id="element-911">{formatForeignCurrency(totalCharge, currency)}</span>
              </div>
            </div>
          </div>
        </div>}

      {activeTab === 'beneficiaries' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-912">
          {beneficiaries.length > 0 ? beneficiaries.map((b, i) => <div key={i} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between" data-id="element-913">
                <div className="flex items-center gap-3" data-id="element-914">
                  <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center text-brand-primary font-bold" data-id="element-915">
                    {b.name.charAt(0)}
                  </div>
                  <div data-id="element-916">
                    <p className="font-semibold text-brand-text-dark text-sm" data-id="element-917">
                      {b.name}
                    </p>
                    <p className="text-xs text-brand-text-light" data-id="element-918">
                      {b.bank} • {b.account.slice(-4)}
                    </p>
                  </div>
                </div>
                <button onClick={() => fillBeneficiary(b)} className="p-2 text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors" title="Send Again" data-id="element-919">
                  <Send size={18} data-id="element-920" />
                </button>
              </div>) : <div className="col-span-full" data-id="element-921">
              <EmptyState icon={Users} title="No saved beneficiaries" description="Recipients will be saved here automatically after your first transfer." data-id="element-922" />
            </div>}
        </div>}

      {activeTab === 'history' && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-923">
          {state.transfers.length > 0 ? <div className="overflow-x-auto" data-id="element-924">
              <table className="w-full text-left text-sm" data-id="element-925">
                <thead className="bg-gray-50 text-brand-text-light border-b border-gray-100" data-id="element-926">
                  <tr data-id="element-927">
                    <th className="px-6 py-4 font-medium" data-id="element-928">Recipient</th>
                    <th className="px-6 py-4 font-medium" data-id="element-929">Date</th>
                    <th className="px-6 py-4 font-medium" data-id="element-930">Sent</th>
                    <th className="px-6 py-4 font-medium" data-id="element-931">Received</th>
                    <th className="px-6 py-4 font-medium" data-id="element-932">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-933">
                  {state.transfers.map(tx => <tr key={tx.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-934">
                      <td className="px-6 py-4" data-id="element-935">
                        <p className="font-medium text-brand-text-dark" data-id="element-936">
                          {tx.recipientName}
                        </p>
                        <p className="text-xs text-brand-text-light" data-id="element-937">
                          {tx.bankName}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-brand-text-light" data-id="element-938">
                        {formatDate(tx.date)}
                      </td>
                      <td className="px-6 py-4 font-medium" data-id="element-939">
                        {formatForeignCurrency(tx.sourceAmount, tx.sourceCurrency)}
                      </td>
                      <td className="px-6 py-4 font-bold text-brand-text-dark" data-id="element-940">
                        {formatCurrency(tx.ngnAmount)}
                      </td>
                      <td className="px-6 py-4" data-id="element-941">
                        <StatusBadge status={tx.status} data-id="element-942" />
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div> : <div className="p-8" data-id="element-943">
              <EmptyState icon={History} title="No transfer history" description="Your international transfers will appear here." data-id="element-944" />
            </div>}
        </div>}

      {/* Confirmation Modal */}
      <Modal isOpen={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} title="Review Transfer" data-id="element-945">
        <div className="space-y-6" data-id="element-946">
          <div className="bg-gray-50 rounded-xl p-4 text-center" data-id="element-947">
            <p className="text-sm text-brand-text-light mb-1" data-id="element-948">
              You are sending
            </p>
            <p className="text-3xl font-bold text-brand-text-dark" data-id="element-949">
              {formatForeignCurrency(totalCharge, currency)}
            </p>
            <p className="text-xs text-brand-text-light mt-1" data-id="element-950">
              Includes {formatForeignCurrency(fee, currency)} fee
            </p>
          </div>

          <div className="space-y-3 text-sm" data-id="element-951">
            <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-952">
              <span className="text-brand-text-light" data-id="element-953">Recipient gets</span>
              <span className="font-bold text-brand-primary" data-id="element-954">
                {formatCurrency(ngnAmount)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-955">
              <span className="text-brand-text-light" data-id="element-956">Recipient Name</span>
              <span className="font-medium text-brand-text-dark" data-id="element-957">
                {recipientName}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-958">
              <span className="text-brand-text-light" data-id="element-959">Bank Details</span>
              <span className="font-medium text-brand-text-dark" data-id="element-960">
                {bankName} - {accountNumber}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-961">
              <span className="text-brand-text-light" data-id="element-962">Exchange Rate</span>
              <span className="font-medium text-brand-text-dark" data-id="element-963">
                1 {currency} = ₦{rate.toLocaleString()}
              </span>
            </div>
          </div>

          <button onClick={confirmTransfer} disabled={isProcessing} className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3.5 rounded-xl font-semibold transition-colors disabled:opacity-70 flex items-center justify-center" data-id="element-964">
            {isProcessing ? 'Processing...' : 'Confirm & Send'}
          </button>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal isOpen={isSuccessOpen} onClose={() => setIsSuccessOpen(false)} title="Transfer Successful" data-id="element-965">
        <div className="text-center py-6" data-id="element-966">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-green-500 mx-auto mb-4" data-id="element-967">
            <CheckCircle2 size={32} data-id="element-968" />
          </div>
          <h3 className="text-xl font-bold text-brand-text-dark mb-2" data-id="element-969">
            Money Sent!
          </h3>
          <p className="text-brand-text-light mb-6" data-id="element-970">
            Your transfer to {recipientName} has been initiated and will arrive
            instantly.
          </p>

          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left" data-id="element-971">
            <p className="text-xs text-brand-text-light mb-1" data-id="element-972">
              Reference Number
            </p>
            <p className="font-mono font-medium text-brand-text-dark" data-id="element-973">
              {currentRef}
            </p>
          </div>

          <button onClick={() => setIsSuccessOpen(false)} className="w-full bg-gray-100 hover:bg-gray-200 text-brand-text-dark py-3 rounded-xl font-semibold transition-colors" data-id="element-974">
            Done
          </button>
        </div>
      </Modal>
    </div>;
};